import { SIM_VERSION } from '@am/sim';
import { currentAccount } from './account';
import { apiUrl } from './server';

/*
 * Crash, error and stall reports: the game tells the server (POST /api/reports, kept by the
 * relay's reports.ts) the moment something goes wrong, with what is needed to find out why:
 *   - errors nobody caught, a layer of the picture failing, the simulation failing, the graphics
 *     device lost, a co-op desync;
 *   - stalls: a frame or a gap between frames long enough to feel (STALL_MS), with how long each
 *     layer of that frame took;
 *   - hangs: the page frozen for good. It cannot say so itself, so a small watchdog worker
 *     (diagnostics.worker.ts) listens for its heartbeat and, when that stops, sends the report
 *     for it, with what the page was doing last (the phase: which layer or step);
 *   - the simulation stalling or taking long over a step.
 * Each report carries the versions, the device and its graphics card, the moment in the game
 * (time, speed, tool, counts), the last things that happened (breadcrumbs) and, where the page
 * can still get one, the world as it was (a save snapshot) to replay it.
 *
 * Players can turn reports off in the settings. In development they are only sent with
 * localStorage am27.diag = '1' (so testing does not fill the server).
 */

declare const __APP_VERSION__: string;

export type ReportKind =
  | 'error'
  | 'stall'
  | 'hang'
  | 'simStall'
  | 'simSlow'
  | 'deviceLost'
  | 'desync';

/** A frame (or a gap between frames) this long is a stall worth reporting (ms). */
export const STALL_MS = 700;
/** The page silent this long is hung (ms; the watchdog's limit). */
export const HANG_MS = 6000;
const CRUMBS = 80;
/** Reports at most per kind in one session, and stalls at least this far apart (ms). */
const PER_KIND = 4;
const STALL_GAP_MS = 90_000;

interface Crumb {
  /** Milliseconds since the page started. */
  at: number;
  what: string;
  /** How many times in a row it happened (once when absent). */
  times?: number;
}

const version = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev';
/** The release this page belongs to (the desktop app's version; 'dev' in development). */
export const APP_VERSION = version;

class Diagnostics {
  /** Off: nothing is sent (the player's choice, see Controls.reports). */
  enabled = true;
  /** The moment in the game, for a report (set by the game while one runs). */
  context: (() => Record<string, unknown>) | null = null;
  /** The world now as save bytes, to send with a report (set by the game). */
  snapshot: (() => Promise<Uint8Array | null>) | null = null;
  /** The graphics card, once known. */
  gpu: Record<string, unknown> = {};
  /**
   * Behind a loading screen (compiling, first frames): long frames there are expected and no
   * stall, and the watchdog waits too (a slow device may take long over a step).
   */
  get loading(): boolean {
    return this.loadingNow;
  }
  set loading(on: boolean) {
    this.loadingNow = on;
    if (!on) this.loadedAt = performance.now();
  }
  private loadingNow = true;
  /** When the loading screen went (the first moments after, the game's panels draw in). */
  private loadedAt = 0;

  private readonly crumbs: Crumb[] = [];
  private readonly sent = new Map<string, number>();
  private readonly perKind = new Map<string, number>();
  private lastStall = -Infinity;
  private watchdog: Worker | null = null;
  private readonly phases = new Map<string, number>();
  private phaseNow = -1;
  private lastBeat = performance.now();
  private contextAt = 0;
  private started = false;

  /** Starts listening: uncaught errors, the heartbeat for the watchdog. Once per page. */
  start(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;
    window.addEventListener('error', (e) => {
      const err = e.error as unknown;
      this.error('window', err ?? e.message);
    });
    window.addEventListener('unhandledrejection', (e) => this.error('promise', e.reason));
    // Every console error becomes a breadcrumb (the layers log theirs there).
    const consoleError = console.error.bind(console);
    console.error = (...args: unknown[]) => {
      const text = args.map(describe).join(' ');
      this.crumb(`console.error ${text.slice(0, 300)}`);
      if (text.includes('WebGPU') || text.includes('GPUValidationError')) this.gpuError(text);
      consoleError(...args);
    };
    try {
      this.watchdog = new Worker(new URL('./diagnostics.worker.ts', import.meta.url), {
        type: 'module',
      });
      this.watchdog.postMessage({ type: 'setup', hangMs: HANG_MS, send: this.allowed() });
    } catch {
      this.watchdog = null; // No watchdog on this device: hangs go unreported.
    }
    window.setInterval(() => this.beat(), 500);
    document.addEventListener('visibilitychange', () => this.beat());
  }

  /** Something happened worth knowing afterwards (a command, a tool, a screen). */
  crumb(what: string): void {
    // The same line again (an error every frame): counted on the last one, not pushed, so
    // the breadcrumbs keep what led up to it.
    const last = this.crumbs[this.crumbs.length - 1];
    if (last && last.what === what) {
      last.times = (last.times ?? 1) + 1;
      return;
    }
    const c: Crumb = { at: Math.round(performance.now()), what };
    this.crumbs.push(c);
    if (this.crumbs.length > CRUMBS) this.crumbs.shift();
    this.watchdog?.postMessage({ type: 'crumb', crumb: c });
  }

  /**
   * What the page is doing now (a layer of the frame, a step of the game): cheap, called often.
   * The watchdog hears it, so a hang can say where it happened.
   */
  phase(name: string): void {
    let id = this.phases.get(name);
    if (id === undefined) {
      id = this.phases.size;
      this.phases.set(name, id);
      this.watchdog?.postMessage({ type: 'name', id, name });
    }
    if (id === this.phaseNow) return;
    this.phaseNow = id;
    this.watchdog?.postMessage(id);
  }

  /** When the graphics card last refused the game's commands (performance.now), and how often. */
  gpuErrorAt = -Infinity;
  gpuErrors = 0;
  private gpuFirst = true;

  /**
   * The graphics card refused something (a WebGPU validation error): the picture stops until it
   * is put right. The first one names the cause (those after say "invalid due to a previous
   * error"), so it goes in a report of its own, with the world and the camera.
   */
  private gpuError(text: string): void {
    this.gpuErrorAt = performance.now();
    this.gpuErrors++;
    if (!this.gpuFirst) return;
    this.gpuFirst = false;
    void this.report(
      'error',
      `gpu: ${text.slice(0, 200)}`,
      { where: 'gpu', detail: text.slice(0, 3000) },
      true,
    );
  }

  /** An error caught somewhere (`where`: what failed). Each different one is sent once. */
  error(where: string, err: unknown, attachWorld = true): void {
    const message = `${where}: ${describe(err)}`.slice(0, 600);
    const stack = err instanceof Error ? (err.stack ?? '').slice(0, 4000) : undefined;
    this.crumb(`error ${message.slice(0, 200)}`);
    void this.report('error', message, { where, stack }, attachWorld);
  }

  /** A frame or gap of `ms` (where: what was running), with each layer's time if known. */
  stall(
    where: string,
    ms: number,
    layers?: readonly (readonly [string, number])[],
    extra: Record<string, unknown> = {},
  ): void {
    if (this.loading || performance.now() - this.loadedAt < 4000) return;
    const now = performance.now();
    if (now - this.lastStall < STALL_GAP_MS) return;
    this.lastStall = now;
    const top = layers
      ? [...layers]
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([n, t]) => [n, Math.round(t)])
      : undefined;
    void this.report(
      'stall',
      `${Math.round(ms)} ms in ${where}`,
      { where, ms, layers: top, ...extra },
      ms > 2000,
    );
  }

  /** Sends a report (at most PER_KIND of each kind, each message once). */
  async report(
    kind: ReportKind,
    message: string,
    extra: Record<string, unknown> = {},
    attachWorld: boolean | Uint8Array = false,
  ): Promise<void> {
    if (!this.allowed()) return;
    const key = `${kind}:${message.slice(0, 160)}`;
    if (this.sent.has(key)) return;
    const n = this.perKind.get(kind) ?? 0;
    if (n >= PER_KIND) return;
    this.sent.set(key, Date.now());
    this.perKind.set(kind, n + 1);
    let snapshot: string | undefined;
    if (attachWorld instanceof Uint8Array) snapshot = base64(attachWorld);
    else if (attachWorld && this.snapshot) {
      try {
        const data = await withTimeout(this.snapshot(), 3000);
        if (data) snapshot = base64(data);
      } catch {
        // The world could not be had (the simulation is down): the report goes without it.
      }
    }
    const body = { kind, message, ...this.base(), ...extra, snapshot };
    try {
      const account = currentAccount();
      await fetch(apiUrl('/reports'), {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(account ? { authorization: `Bearer ${account.token}` } : {}),
        },
        body: JSON.stringify(body),
        keepalive: !snapshot,
      });
    } catch {
      // Offline: the report is lost, nothing to be done.
    }
  }

  /** Versions, device, the moment in the game and the breadcrumbs. */
  private base(): Record<string, unknown> {
    let game: Record<string, unknown> = {};
    try {
      game = this.context?.() ?? {};
    } catch {
      // A broken context must not stop the report.
    }
    return {
      version,
      simVersion: SIM_VERSION,
      dev: import.meta.env.DEV,
      desktop: navigator.userAgent.includes('Electron'),
      device: {
        ua: navigator.userAgent,
        lang: navigator.language,
        cores: navigator.hardwareConcurrency,
        memory: (navigator as { deviceMemory?: number }).deviceMemory,
        screen: `${screen.width}x${screen.height}@${window.devicePixelRatio}`,
        visible: document.visibilityState,
        gpu: this.gpu,
      },
      uptimeS: Math.round(performance.now() / 1000),
      game,
      crumbs: [...this.crumbs],
    };
  }

  private allowed(): boolean {
    if (!this.enabled) return false;
    if (!import.meta.env.DEV) return true;
    try {
      return localStorage.getItem('am27.diag') === '1';
    } catch {
      return false;
    }
  }

  /** Twice a second: the heartbeat, and a stall between frames seen afterwards. */
  private beat(): void {
    const now = performance.now();
    const gap = now - this.lastBeat;
    this.lastBeat = now;
    const visible = document.visibilityState === 'visible';
    // The page was blocked (not just hidden or asleep: then the timer is slowed, not late).
    if (visible && gap > STALL_MS + 500 && gap < 5 * 60_000) {
      const name = [...this.phases].find(([, id]) => id === this.phaseNow)?.[0] ?? 'idle';
      this.stall(`page (last in ${name})`, gap - 500);
    }
    // The watchdog gets the moment in the game now and then, to send if the page hangs.
    let context: Record<string, unknown> | undefined;
    if (now - this.contextAt > 2000) {
      this.contextAt = now;
      try {
        context = { ...this.base(), crumbs: undefined };
      } catch {
        context = undefined;
      }
    }
    this.watchdog?.postMessage({
      type: 'beat',
      visible,
      active: this.context !== null && !this.loading,
      send: this.allowed(),
      context,
      user: currentAccount()?.username ?? null,
    });
  }
}

function describe(v: unknown): string {
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v)?.slice(0, 300) ?? String(v);
  } catch {
    return String(v);
  }
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

function base64(data: Uint8Array): string {
  let s = '';
  for (let i = 0; i < data.length; i += 0x8000) {
    s += String.fromCharCode(...data.subarray(i, i + 0x8000));
  }
  return btoa(s);
}

/** The page's one set of diagnostics. */
export const diag = new Diagnostics();
